import type { NostrEvent } from '@nostrify/nostrify';

import { KIND_DOC_METADATA, docAltTag } from './kinds';
import type { DocCollaborator, DocMetadata, DocRole, DocSummary } from './types';

/**
 * Parse and validate a kind 38549 document metadata event.
 * Returns undefined for malformed events.
 */
export function parseDocMetadata(event: NostrEvent): DocMetadata | undefined {
  if (event.kind !== KIND_DOC_METADATA) return undefined;

  const docId = event.tags.find(([name]) => name === 'd')?.[1];
  if (!docId) return undefined;

  const title = event.tags.find(([name]) => name === 'title')?.[1] ?? '';
  const createdAtTag = event.tags.find(([name]) => name === 'created')?.[1];
  const createdAt = createdAtTag && /^\d+$/.test(createdAtTag)
    ? Number(createdAtTag)
    : event.created_at;
  const deleted = event.tags.some(([name, value]) => name === 'status' && value === 'deleted');

  const collaborators: DocCollaborator[] = [];
  const seen = new Set<string>();
  for (const tag of event.tags) {
    if (tag[0] !== 'p') continue;
    const pubkey = tag[1];
    if (!/^[0-9a-f]{64}$/.test(pubkey) || seen.has(pubkey)) continue;
    if (pubkey === event.pubkey) continue;
    seen.add(pubkey);
    const role = tag[3] === 'editor' ? 'editor' : 'viewer';
    collaborators.push({ pubkey, role });
  }

  return {
    ownerPubkey: event.pubkey,
    docId,
    title,
    createdAt,
    updatedAt: event.created_at,
    deleted,
    collaborators,
  };
}

/** Build an unsigned kind 38549 event template. */
export function buildDocMetadataEvent(opts: {
  docId: string;
  title: string;
  collaborators: DocCollaborator[];
  createdAt: number;
  deleted?: boolean;
}): {
  kind: number;
  content: string;
  tags: string[][];
  created_at: number;
} {
  const tags: string[][] = [
    ['d', opts.docId],
    ['title', opts.title],
    docAltTag(opts.title),
    ['created', String(opts.createdAt)],
  ];

  for (const { pubkey, role } of opts.collaborators) {
    // p tag: [p, pubkey, relay-url?, petname?] — role is carried in the
    // 4th position (petname slot), see NIP.md.
    tags.push(['p', pubkey, '', role]);
  }

  if (opts.deleted) {
    tags.push(['status', 'deleted']);
  }

  return {
    kind: KIND_DOC_METADATA,
    content: '',
    tags,
    created_at: Math.floor(Date.now() / 1000),
  };
}

/** Resolve the effective role of a pubkey for a document. */
export function roleForPubkey(pubkey: string | undefined, metadata: DocMetadata | undefined): DocRole {
  if (!metadata || !pubkey) return 'viewer';
  if (metadata.ownerPubkey === pubkey) return 'owner';
  return metadata.collaborators.find((c) => c.pubkey === pubkey)?.role ?? 'viewer';
}

/** Whether a pubkey may publish content updates for the document. */
export function canPubkeyEdit(pubkey: string, metadata: DocMetadata | undefined): boolean {
  if (!metadata) return false;
  if (metadata.ownerPubkey === pubkey) return true;
  return metadata.collaborators.some((c) => c.pubkey === pubkey && c.role === 'editor');
}

/** Whether a pubkey is allowed to be present in the document (any known role). */
export function isPubkeyKnown(pubkey: string, metadata: DocMetadata): boolean {
  if (metadata.ownerPubkey === pubkey) return true;
  return metadata.collaborators.some((c) => c.pubkey === pubkey);
}

/** Convert metadata into a dashboard summary for the given viewer. */
export function toDocSummary(metadata: DocMetadata, viewerPubkey: string | undefined): DocSummary {
  const role = roleForPubkey(viewerPubkey, metadata);
  return {
    ownerPubkey: metadata.ownerPubkey,
    docId: metadata.docId,
    title: metadata.title || 'Untitled document',
    updatedAt: metadata.updatedAt,
    createdAt: metadata.createdAt,
    deleted: metadata.deleted,
    collaboratorPubkeys: metadata.collaborators.map((c) => c.pubkey),
    role,
  };
}

/** Pick the latest valid metadata event from a list (dedupes addressable replacements). */
export function latestMetadata(events: NostrEvent[]): DocMetadata | undefined {
  let latest: DocMetadata | undefined;
  for (const event of events) {
    const metadata = parseDocMetadata(event);
    if (!metadata) continue;
    if (!latest || metadata.updatedAt > latest.updatedAt) {
      latest = metadata;
    }
  }
  return latest;
}
